import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import type { LoanOpsChatSessionSummary } from '../../api/loanOpsChat';
import {
  CHAT_HISTORY_WIDTH,
  formatChatTimestamp,
  groupSessionsByRecency,
} from './loanOpsChatUi';

export function LoanOpsChatHistory({
  sessions,
  activeSessionId,
  onSelect,
  onNewChat,
}: {
  sessions: LoanOpsChatSessionSummary[];
  activeSessionId?: string;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? sessions.filter((s) => (s.title || 'Chat').toLowerCase().includes(q))
      : sessions;
    return groupSessionsByRecency(filtered);
  }, [sessions, query]);

  return (
    <Box
      component="aside"
      aria-label="Chat history"
      sx={{
        width: CHAT_HISTORY_WIDTH,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: isLight
          ? alpha(theme.palette.primary.main, 0.03)
          : alpha(theme.palette.common.black, 0.25),
      }}
    >
      <Box sx={{ p: 1, pb: 0.75, flexShrink: 0 }}>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<AddCommentRoundedIcon />}
          onClick={onNewChat}
          sx={{ justifyContent: 'flex-start', mb: 0.85, borderRadius: 1.5 }}
        >
          New chat
        </Button>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chats"
          inputProps={{ 'aria-label': 'Search chat history' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.78rem' } }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 0.75, pb: 1 }}>
        {groups.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', p: 1.5, lineHeight: 1.5 }}
          >
            {sessions.length === 0 ? 'No past sessions yet.' : 'No chats match that search.'}
          </Typography>
        )}

        {groups.map((group) => (
          <List key={group.label} dense disablePadding sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                px: 1,
                pt: 1,
                pb: 0.35,
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {group.label}
            </Typography>

            {group.sessions.map((s) => {
              const active = s.sessionId === activeSessionId;
              return (
                <ListItemButton
                  key={s.sessionId}
                  selected={active}
                  onClick={() => onSelect(s.sessionId)}
                  sx={{
                    alignItems: 'flex-start',
                    borderRadius: 1.5,
                    mb: 0.25,
                    py: 0.75,
                    px: 1,
                    borderLeft: '2px solid',
                    borderLeftColor: active ? 'primary.main' : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, isLight ? 0.09 : 0.2),
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: alpha(theme.palette.primary.main, isLight ? 0.14 : 0.26),
                    },
                  }}
                >
                  <ListItemText
                    primary={s.title || 'Chat'}
                    secondary={formatChatTimestamp(s.updatedAt)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '0.78rem',
                      fontWeight: active ? 700 : 500,
                      color: active ? 'primary.main' : 'text.primary',
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.66rem' }}
                    sx={{ my: 0 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>
    </Box>
  );
}
